// src/pages/Instructions.jsx
import { useState } from 'react';
import { useApp } from '../AppContext';
import { Card } from '../components/UI';

const sections = [
  {
    group: 'Guard',
    items: [
      {
        id: 'signin',
        title: 'Sign-In Process',
        content: `The sign-in terminal is the main screen guards use throughout the day.

1. Scan the member's QR code — their name, type, and family load automatically. If they don't have their card, use the Manual Lookup section below the scanner.
2. Check caregiver status — if the membership has a caregiver on file, a toggle appears.
3. Set Members Today — how many people from this membership are entering.
4. Enter guest count — if the member has guests, enter the number. The total is calculated automatically.
5. Select Cash or Card for guest payment.
6. Enter your name in the Guard Name field.
7. Press Complete Sign-In.`
      },
      {
        id: 'lookup',
        title: 'Manual Member Lookup',
        content: `If a member doesn't have their card, use the Manual Member Lookup section below the QR scanner.

Type their last name or membership number — results appear as you type. Click any result to load their details into the form exactly as if you had scanned their card.

Always verify their identity without a card — ask to see photo ID or confirm their address or membership type before signing them in.`
      },
      {
        id: 'guests',
        title: 'Handling Guests',
        content: `Guests are people not on any membership who are accompanying a member. Members are always free.

Guest fees are set per site — check the total box before completing sign-in.

Cash and card rates differ slightly. Select the correct payment method before submitting.`
      },
      {
        id: 'junior',
        title: 'Junior Memberships',
        content: `Junior memberships cannot bring guests. When a Junior member is loaded, the Guests field is automatically disabled and a notice appears.

This cannot be overridden at the terminal. If a Junior member needs a guest for a specific reason, a manager must handle it separately.`
      },
      {
        id: 'party-flag',
        title: 'Party Flag Warning',
        content: `When a member arrives with 6 or more guests, a warning banner appears and the Submit button is locked.

You must check the acknowledgment checkbox before proceeding. If the group doesn't need a party booking, a manager can use the override option.`
      },
      {
        id: 'departures',
        title: 'Recording Departures',
        content: `When a member leaves, find their entry in the Sign-In Log, click it to expand, and press Record Departure. This stamps the current time.`
      },
      {
        id: 'undo',
        title: 'Undoing a Sign-In',
        content: `Press Undo Last Sign-In below the submit button to reverse the most recent sign-in. This only works for the most recent transaction.

To correct older entries, ask a manager to edit in the Manager Report.`
      },
    ]
  },
  {
    group: 'Manager',
    items: [
      {
        id: 'parties',
        title: 'Party Bookings',
        content: `Party bookings track groups using the facility for private events. Go to the Parties page to create a new booking.

Member Pays — one total for the entire party.
Guests Pay — each group pays separately at check-in.

Pricing: swimmers, non-swimmers, charcoal bags, and a venue fee per 25 guests are all tracked.`
      },
      {
        id: 'checkin',
        title: 'Party Check-In',
        content: `On the day of the party, open the party and use the Check-In view to track arrivals. Each guest group row has a Mark Arrived button. The attendance bar shows how many groups have arrived and the running total collected.`
      },
      {
        id: 'member-status',
        title: 'Member Status',
        content: `The Members page shows all members and lets you change their status instantly.

Active — paid, current member. Card scans are allowed.
Inactive — dues not confirmed. Card is blocked with an amber warning.
Resigned — membership ended. Card is blocked with a red warning.

When a member pays, press Active to restore their access immediately. You can also delete a member permanently — this removes all their sign-in history and cannot be undone.`
      },
      {
        id: 'reports',
        title: 'Reports',
        content: `The Manager Report has up to four tabs:

Sign-In Records — every transaction with filtering and CSV/PDF export.
Party Records — all party bookings with download links.
Guest Log — scanned guest history (when Guest ID is enabled).
Revenue — income breakdown by walk-ins and parties, cash/card split.`
      },
    ]
  },
  {
    group: 'Cards & Import',
    items: [
      {
        id: 'cards',
        title: 'Generating Member Cards',
        content: `Go to the Code Generator (Cards in the nav) to create QR codes.

Cards are colour-coded by type: Family = Blue, Junior = Amber, Single = Green, Couple = Purple.

Fill in member details and click Generate. The card is saved to the database immediately.`
      },
      {
        id: 'batch',
        title: 'Batch Printing',
        content: `After generating a card, set the Copies count and click Add to Batch. Repeat for each member.

Click Print All to print every card at once. Cards print 2 per row on a letter page.

For families, add 3 copies to give both parents and a caregiver their own card.`
      },
      {
        id: 'import',
        title: 'Bulk Member Import',
        content: `At the start of each season, go to the Import page and upload your membership CSV.

New members get the next number above the current maximum — existing numbers are never shifted.

Resigned members are imported with resigned status. Existing members are matched and skipped. If a returning member is now resigned, their status is updated automatically.`
      },
    ]
  },
  {
    group: 'Account',
    items: [
      {
        id: 'pin',
        title: 'PIN Login',
        content: `Every account can have a 4-digit PIN alongside its password. On the login screen, click PIN to switch to the keypad — or just type 4 digits on the keyboard and the form submits automatically.

PINs are stored encrypted. Set or change yours from Account Settings.

Admins and superadmins cannot use PINs — password only.`
      },
      {
        id: '2fa',
        title: 'Two-Factor Authentication',
        content: `2FA adds a second layer of security. After your password, you enter a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password, etc.).

To set up: go to Account Settings and click Set Up 2FA. Scan the QR code, enter the code to confirm, then save your backup codes — they're shown once only.

Some roles require 2FA and it cannot be disabled for those accounts.`
      },
    ]
  },
  {
    group: 'Troubleshooting',
    items: [
      {
        id: 'qr-issue',
        title: 'QR Code Not Scanning',
        content: `Hold the card steady with the scanner beam covering the full QR code. If it scans but shows an error, the member may not be in the database — ask a manager to check their status.`
      },
      {
        id: 'member-blocked',
        title: 'Member Blocked — Inactive',
        content: `The member's dues haven't been confirmed. A manager can mark them as Active on the Member Status page. The change is immediate.`
      },
      {
        id: 'member-resigned',
        title: 'Member Blocked — Resigned',
        content: `The membership has been ended. Refer them to the club office.`
      },
      {
        id: 'no-submit',
        title: 'Submit Button Greyed Out',
        content: `If 6+ guests are entered, check the acknowledgment checkbox before Submit becomes active. Managers also have an override option.`
      },
      {
        id: 'wrong-signin',
        title: 'Wrong Sign-In Submitted',
        content: `Use Undo Last Sign-In immediately. If the session has moved on, ask a manager to correct it in the Sign-In Log or Manager Report.`
      },
      {
        id: 'lost-2fa',
        title: 'Lost 2FA Device',
        content: `Use a backup code on the 2FA entry screen. If you have no backup codes left, contact a superadmin to reset your 2FA from the Admin Panel.`
      },
    ]
  }
];

export default function Instructions() {
  const { site } = useApp();
  const [active, setActive] = useState('signin');

  const allItems = sections.flatMap(s => s.items);
  const current = allItems.find(i => i.id === active);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 52px)', fontFamily: 'var(--font)' }}>
      {/* Sidebar */}
      <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg2)' }}>
        {sections.map(s => (
          <div key={s.group}>
            <div style={{ padding: '14px 16px 4px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg4)' }}>{s.group}</div>
            {s.items.map(item => (
              <button key={item.id} onClick={() => setActive(item.id)} style={{
                display: 'block', width: '100%', padding: '8px 16px',
                textAlign: 'left', background: 'none', border: 'none',
                cursor: 'pointer', fontFamily: 'var(--font)',
                fontSize: 13, fontWeight: active === item.id ? 600 : 400,
                color: active === item.id ? 'var(--accent)' : 'var(--fg2)',
                borderLeft: `2px solid ${active === item.id ? 'var(--accent)' : 'transparent'}`,
                borderRadius: 0,
              }}
                onMouseEnter={e => { if (active !== item.id) e.currentTarget.style.color = 'var(--fg)'; }}
                onMouseLeave={e => { if (active !== item.id) e.currentTarget.style.color = 'var(--fg2)'; }}
              >
                {item.title}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', maxWidth: 760 }}>
        {current && (
          <div key={active} style={{ animation: 'fadeIn 0.15s ease' }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.02em', marginBottom: 20 }}>{current.title}</h1>
            <div style={{ fontSize: 14, color: 'var(--fg2)', lineHeight: 1.8 }}>
              {current.content.split('\n\n').map((para, i) => {
                if (para.match(/^\d+\./m)) {
                  // Numbered list
                  return (
                    <ol key={i} style={{ paddingLeft: 20, marginBottom: 16 }}>
                      {para.split('\n').filter(l => l.trim()).map((line, j) => (
                        <li key={j} style={{ marginBottom: 8 }}>{line.replace(/^\d+\.\s*/, '')}</li>
                      ))}
                    </ol>
                  );
                }
                return <p key={i} style={{ marginBottom: 14 }}>{para}</p>;
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
